import React, { useState } from "react";
import { Download, FileText, Banknote, ListChecks, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, getBackendBase } from "@/lib/api";

const BACKEND_URL = getBackendBase();  // Phase APEX-C — same-origin only. See lib/api.js.

function fmtDay(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return iso;
    }
}

function docLabel(t, contract) {
    if (t === "ach_authorization") return "ACH Authorization";
    if (t === "coi") return "Certificate of Insurance";
    if (t === "personal_guaranty") return "Personal Guaranty";
    // Phase N.9b-hotfix (2026-02) — Appendix A is a companion doc
    // attached to the contract, not the master rental agreement. Its
    // own label prevents it from being rendered as a second "Rental
    // Agreement" row (which was the visible symptom of the N.9a
    // ship + backfill against 89 prod contracts).
    if (t === "appendix_a") return "Appendix A — Leased Equipment";
    // For the main contract document, fall back to a human label
    // derived from the contract's ``type`` field so admins who created
    // e.g. a "Trailer Lease Agreement" see that exact label instead of
    // a generic "Rental Agreement".
    const typeLabels = {
        rental_agreement: "Rental Agreement",
        lease_agreement: "Lease Agreement",
        trailer_lease_agreement: "Trailer Lease Agreement",
        msa: "Master Service Agreement",
        addendum: "Addendum",
    };
    const ct = (contract && contract.type) || "";
    if (typeLabels[ct]) return typeLabels[ct];
    // Last-resort: title-case the contract.type string.
    if (ct) return ct.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
    return "Lease Agreement";
}

// Phase N.9b-hotfix — canonical render order regardless of the order
// documents were appended to ``contract.documents[]``. Anything not in
// this list keeps its incoming order at the tail.
const DOC_ORDER = ["rental_agreement", "appendix_a", "ach_authorization", "coi", "personal_guaranty"];
function docRank(t) {
    const idx = DOC_ORDER.indexOf(t);
    return idx === -1 ? DOC_ORDER.length : idx;
}

/**
 * Phase F-trust follow-up — per-document download list for the customer
 * dashboard. One row per document with type label, signed-at, per-doc trust
 * pill (green when `rfc3161_status==="ok"`, amber otherwise), and a download
 * button that streams the signed PDF via /api/customer/contracts/{id}/pdf.
 *
 * For legacy single-doc contracts (no documents[] from the server, or the
 * synthesized fallback), the row count collapses to 1 — UX matches the old
 * single-button experience.
 *
 * Props:
 *   contract: customer contract dict from /api/customer/contracts
 */
export function CustomerContractDocsList({ contract }) {
    const [busyId, setBusyId] = useState(null);

    if (!contract || contract.status !== "fully_signed") return null;

    // Build the row list from contract.documents[]; fall back to a single
    // synthesized rental row if the contract is legacy / pre-Phase-F.
    let rows = (contract.documents || []).map((d) => ({
        doc_id: d.doc_id,
        doc_type: d.doc_type,
        filename: d.filename,
        rfc3161_status: d.rfc3161_status,
        signed_at_per_tsa: d.signed_at_per_tsa,
        sha: d.signed_pdf_sha256 || d.generated_pdf_sha256,
        generated_at: d.generated_at,
        is_companion: !!d.is_companion,
    }));
    if (rows.length === 0) {
        rows = [{
            doc_id: null,
            doc_type: "rental_agreement",
            filename: null,
            rfc3161_status: contract.rfc3161_status === "valid" ? "ok" : (contract.rfc3161_status || "none"),
            signed_at_per_tsa: contract.signed_at_per_tsa,
            sha: contract.signed_pdf_sha256,
            generated_at: null,
            is_companion: false,
        }];
    }
    // Phase N.9b-hotfix v2 (2026-02) — strict dedupe by doc_id (NOT
    // by doc_type label). If two entries have the same doc_id (should
    // never happen server-side but defence in depth), collapse to the
    // first. If two entries have the SAME doc_type but different
    // doc_ids (legitimate: contract regenerated + old copy retained),
    // KEEP BOTH — the click handler binds to the row's own doc_id so
    // each Download button hits the right bytes.
    {
        const seen = new Set();
        rows = rows.filter((r) => {
            const k = r.doc_id || `nodocid:${r.doc_type}`;
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    }
    // Phase N.9b-hotfix — canonical render order. Ties (two rows of
    // same doc_type) retain their incoming order (stable sort).
    rows = rows.slice().sort((a, b) => docRank(a.doc_type) - docRank(b.doc_type));
    // Phase N.9b-hotfix v2 — disambiguate labels when two rows share
    // the same doc_type (rare but possible when a contract's PDF was
    // regenerated + the old row was retained for audit).
    {
        const dtCounts = rows.reduce((acc, r) => {
            acc[r.doc_type] = (acc[r.doc_type] || 0) + 1;
            return acc;
        }, {});
        const dtSeen = {};
        rows = rows.map((r) => {
            if (dtCounts[r.doc_type] > 1) {
                dtSeen[r.doc_type] = (dtSeen[r.doc_type] || 0) + 1;
                const suffix = r.filename || `#${dtSeen[r.doc_type]} · ${(r.doc_id || "").slice(-8)}`;
                return { ...r, _label_suffix: suffix };
            }
            return r;
        });
    }

    const downloadRow = async (row) => {
        const key = row.doc_id || "primary";
        setBusyId(key);
        try {
            // Fetch as a blob via axios so we can read the integrity header
            // before the browser swallows the response into a download.
            const resp = await api.get(`/customer/contracts/${contract.id}/pdf`, {
                params: row.doc_id ? { doc_id: row.doc_id } : {},
                responseType: "blob",
            });
            const integrity = resp.headers?.["x-rentflow-integrity"];
            if (integrity === "tampered") {
                toast.warning("Integrity warning: this signed PDF doesn't match the recorded hash. Contact your account manager.");
            }
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const safeTitle = (contract.title || "contract").replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-|-$/g, "") || "contract";
            const docKebab = (row.doc_type || "doc").replace(/_/g, "-");
            a.href = url;
            a.download = `${safeTitle}-${docKebab}-signed.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="mt-3 border-t border-slate-100 pt-3 space-y-1.5"
            data-testid={`customer-contract-docs-${contract.id}`}>
            <p className="text-[10px] font-medium tracking-[0.18em] uppercase text-slate-500">Documents</p>
            {rows.map((row) => {
                // Phase CUST-UX-FIX (2026-07-08) — the pill now reflects
                // CONTRACT-LEVEL signing state, not the per-document RFC3161
                // timestamp status. Both docs on a fully-signed contract
                // show ``Verified`` uniformly. Rationale: customers read
                // "Verified" as "legally signed", not "TSA-timestamped".
                // The prior per-doc rfc3161 gating caused the Rental
                // Agreement to show amber "Pending" next to a green ACH
                // "Verified" on the SAME fully-countersigned contract,
                // even though both were legally executed in the same
                // signing session. See CustomerDashboardPage — this
                // component is only rendered when contract.status ===
                // "fully_signed", so ``ok`` is always true here.
                const ok = contract.status === "fully_signed";
                // Phase N.9b-hotfix — icon by doc_type.
                const Icon = row.doc_type === "ach_authorization"
                    ? Banknote
                    : row.doc_type === "appendix_a"
                        ? ListChecks
                        : FileText;
                // Appendix A is generated, not signed — show "Attached"
                // instead of "Signed" to avoid claiming a signature that
                // never happened.
                const dateLabel = row.doc_type === "appendix_a" ? "Attached" : "Signed";
                const dateIso = row.doc_type === "appendix_a"
                    ? (row.generated_at || null)
                    : (row.signed_at_per_tsa || contract.admin_signed_at);
                const key = row.doc_id || "primary";
                // Phase N.9b-hotfix v2 — cheap forensic aid: show the
                // last 8 chars of the doc_id on hover so ops (or a
                // support engineer walking a customer through a click)
                // can immediately correlate a rendered row with a
                // manifest entry. Non-intrusive; visible only via the
                // native browser title tooltip.
                const idTip = row.doc_id ? `doc_id …${String(row.doc_id).slice(-8)}` : "no doc_id";
                const labelSuffix = row._label_suffix ? ` (${row._label_suffix})` : "";
                return (
                    <div
                        key={key}
                        data-testid={`customer-contract-doc-row-${row.doc_type}-${contract.id}`}
                        data-doc-id={row.doc_id || ""}
                        title={idTip}
                        className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                            <Icon size={14} className="text-slate-500 shrink-0" />
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-slate-800 truncate">{docLabel(row.doc_type, contract)}{labelSuffix}</p>
                                <p className="text-[10px] text-slate-500">{dateLabel} {fmtDay(dateIso)}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <span className={
                                "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset " +
                                (ok
                                    ? "bg-emerald-50 text-emerald-900 ring-emerald-200"
                                    : "bg-amber-50 text-amber-900 ring-amber-200")
                            }
                                data-testid={`customer-contract-doc-pill-${row.doc_type}-${contract.id}`}>
                                {ok ? <ShieldCheck size={9} /> : <ShieldAlert size={9} />}
                                {ok ? "Verified" : "Pending"}
                            </span>
                            <button
                                type="button"
                                onClick={() => downloadRow(row)}
                                disabled={busyId === key}
                                data-testid={`customer-contract-doc-download-${row.doc_type}-${contract.id}`}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                                <Download size={11} />
                                {busyId === key ? "…" : "Download PDF"}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default CustomerContractDocsList;
